import React, { useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import PageLayout from "../../components/layout/PageLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import API_URL from "../../config";
import { fetchWithAuthUpload } from "../../Context/UserContext";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";

const ALLOWED_EXTENSIONS = [
  "jpg", "jpeg", "png", "pdf", "docx", "doc", "xlsx", "xls", "csv", "txt", "pptx", "ppt", "zip", "rar", "7z",
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const SubirComprobante = () => {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const instruccionesPago = location.state?.instruccionesPago;

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enviado, setEnviado] = useState(false);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const extension = selected.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      toast.error("Formato de archivo no permitido.");
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      toast.error("El archivo excede el tamaño permitido (10MB).");
      return;
    }
    setFile(selected);
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Selecciona un archivo primero.");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("id_tender", tenderId);

      const response = await fetchWithAuthUpload(`${API_URL}/upload/upload-payment-receipt`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        toast.error(data.error || "No se pudo enviar el comprobante.");
        return;
      }

      setEnviado(true);
      toast.success("Comprobante enviado. El equipo de Procu lo revisará pronto.");
    } catch (error) {
      console.error("Error al subir comprobante:", error);
      toast.error("Hubo un error al enviar el comprobante.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <Helmet>
        <title>Subir comprobante de pago - Procu</title>
      </Helmet>

      <div className="mx-auto max-w-xl px-4 py-10 sm:px-6">
        <h1 className="text-2xl font-semibold text-foreground">Tu licitación está casi lista</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Alcanzaste el límite de licitaciones gratuitas activas. Realiza la transferencia y sube
          tu comprobante — apenas el equipo de Procu lo confirme, tu licitación se publicará.
        </p>

        {instruccionesPago && (
          <Card className="mt-6">
            <CardContent className="space-y-1 pt-6 text-sm">
              <p><strong className="text-foreground">Banco:</strong> {instruccionesPago.banco}</p>
              <p><strong className="text-foreground">Tipo de cuenta:</strong> {instruccionesPago.tipo_cuenta}</p>
              <p><strong className="text-foreground">Número de cuenta:</strong> {instruccionesPago.numero_cuenta}</p>
              <p><strong className="text-foreground">CCI:</strong> {instruccionesPago.cci}</p>
              <p><strong className="text-foreground">Titular:</strong> {instruccionesPago.titular}</p>
              <p><strong className="text-foreground">Monto sugerido:</strong> {instruccionesPago.monto_sugerido}</p>
              <p className="pt-2 text-muted-foreground">{instruccionesPago.instrucciones}</p>
            </CardContent>
          </Card>
        )}

        {enviado ? (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-foreground">
              Tu comprobante fue enviado. El equipo de Procu lo revisará y activará tu licitación pronto.
            </p>
            <Button onClick={() => navigate("/mis-licitaciones")}>Ir a mis licitaciones</Button>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <Input type="file" onChange={handleFileChange} disabled={loading} />
            {file && (
              <p className="text-xs text-muted-foreground">Archivo seleccionado: {file.name}</p>
            )}
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? "Enviando..." : "Enviar comprobante"}
            </Button>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default SubirComprobante;
